import { action, computed, observable, makeObservable } from 'mobx';

export enum EFetchState {
  FAILURE = 'FAILURE',
  INITIAL = 'INITIAL',
  START = 'START',
  SUCCESS = 'SUCCESS',
}

export interface IFetch {
  error?: boolean;
  fetched?: boolean;
  fetching?: boolean;
  success?: boolean;
}

export interface INotification {
  save: IFetch;
  finalize: IFetch;
  sendRequest: IFetch;
  authorization: IFetch;
}

export class NotificationService {
  private static readonly states: { [key in EFetchState]: IFetch } = {
    [EFetchState.FAILURE]: { fetched: true, error: true },
    [EFetchState.INITIAL]: { error: false, fetched: false, fetching: false, success: false },
    [EFetchState.START]: { fetching: true },
    [EFetchState.SUCCESS]: { fetched: true, success: true },
  };

  constructor() {
    makeObservable<NotificationService, "state">(this, {
      state: observable,
      isFetchingAny: computed,
      authState: computed,
      finalizeState: computed,
      sendState: computed,
      saveState: computed,
      resetAllButAuthState: action,
      resetAuthState: action,
      setAuthState: action,
      setFinalizeState: action,
      setSaveState: action,
      setSendState: action
    });
  }

  private static generateState(newState: EFetchState) {
    return { ...NotificationService.states.INITIAL, ...NotificationService.states[newState] };
  }

  private state: INotification = {
    authorization: observable({ ...NotificationService.states.INITIAL }),
    finalize: observable({ ...NotificationService.states.INITIAL }),
    save: observable({ ...NotificationService.states.INITIAL }),
    sendRequest: observable({ ...NotificationService.states.INITIAL }),
  };

  // getters
  get isFetchingAny(): boolean {
    return !!(
      this.state.authorization.fetching ||
      this.state.finalize.fetching ||
      this.state.save.fetching ||
      this.state.sendRequest.fetching
    );
  }

  get authState(): IFetch {
    return this.state.authorization;
  }

  get finalizeState(): IFetch {
    return this.state.finalize;
  }

  get sendState(): IFetch {
    return this.state.sendRequest;
  }

  get saveState(): IFetch {
    return this.state.save;
  }

  // reset
  public resetAllButAuthState() {
    this.state.finalize = { ...NotificationService.states.INITIAL };
    this.state.save = { ...NotificationService.states.INITIAL };
    this.state.sendRequest = { ...NotificationService.states.INITIAL };
  }

  public resetAuthState() {
    this.state.authorization = { ...NotificationService.states.INITIAL };
  }

  public setAuthState(newState: EFetchState) {
    this.state.authorization = NotificationService.generateState(newState);
  }

  public setFinalizeState(newState: EFetchState) {
    this.state.finalize = NotificationService.generateState(newState);
  }

  public setSaveState(newState: EFetchState) {
    this.state.save = NotificationService.generateState(newState);
  }

  public setSendState(newState: EFetchState) {
    this.state.sendRequest = NotificationService.generateState(newState);
  }
}
